import { Component, OnInit } from '@angular/core';

import { of as observableOf } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '../../environments/environment';

import { ExhibitorService, Exhibitor } from '../services/exhibitor.service';

/**
 * Read-only download list of exhibitor videos for the /home "Videos" tab.
 *
 * Lists every exhibitor that has at least one video — the Image Video
 * (`data3`, served from images/exhibitor/ivideos/) and/or the Presentation
 * (Evening) Video (`evening_video`, served from images/exhibitor/evideos/) —
 * showing the exhibitor name, brand logo and profile image with download links.
 * All data comes from the existing GET /v3/exhibitor list endpoint.
 */
@Component({
  selector: 'app-videos-view',
  templateUrl: './videos-view.component.html',
  styleUrls: ['./videos-view.component.scss'],
})
export class VideosViewComponent implements OnInit {
  // base path for exhibitor assets (logo, profile image and the video folders)
  readonly imageStorage = environment.eplusSystem + '/_fileDB/images/exhibitor/';

  isLoading = true;

  /** All exhibitors that have at least one video. */
  exhibitors: Exhibitor[] = [];

  /** Free-text filter applied client-side to the loaded list. */
  searchValue = '';

  // ---- video preview dialog --------------------------------------------
  previewOpen = false;
  previewUrl = '';
  previewMime = '';
  previewTitle = '';
  previewLabel = '';

  constructor(private exhibitorService: ExhibitorService) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading = true;
    this.exhibitorService
      .getExhibitors(1, 1000, 'organisation', 'asc', '', '')
      .pipe(
        catchError(() => observableOf(null)),
        map((res) => (res ? res.data || [] : []))
      )
      .subscribe((rows) => {
        this.exhibitors = rows.filter((e) => this.hasImageVideo(e) || this.hasPresentationVideo(e));
        this.isLoading = false;
      });
  }

  /** Exhibitors matching the current search term (by company/contact name). */
  get filteredExhibitors(): Exhibitor[] {
    const term = (this.searchValue || '').trim().toLowerCase();
    if (!term) return this.exhibitors;
    return this.exhibitors.filter((e) => {
      const haystack = `${e.organisation || ''} ${e.name || ''} ${e.vorname || ''}`.toLowerCase();
      return haystack.includes(term);
    });
  }

  hasImageVideo(e: Exhibitor): boolean {
    return !!(e.data3 && e.data3.trim());
  }

  hasPresentationVideo(e: Exhibitor): boolean {
    return !!(e.evening_video && e.evening_video.trim());
  }

  displayName(e: Exhibitor): string {
    return (e.organisation && e.organisation.trim()) || (e.name && e.name.trim()) || '—';
  }

  contactName(e: Exhibitor): string {
    return `${e.vorname || ''} ${e.name || ''}`.trim();
  }

  /**
   * Percent-encode a stored filename so URLs with spaces, accents or other
   * special characters resolve correctly. Stored names keep spaces (e.g.
   * "Video Promo UK 3.mp4-<uuid>.mp4"), which break a raw <video>/<a> src.
   */
  private encodeName(filename: string): string {
    return encodeURIComponent(filename || '');
  }

  logoUrl(e: Exhibitor): string {
    return e.logo ? `${this.imageStorage}${this.encodeName(e.logo)}` : '';
  }

  profileUrl(e: Exhibitor): string {
    return e.profile_image ? `${this.imageStorage}${this.encodeName(e.profile_image)}` : '';
  }

  imageVideoUrl(e: Exhibitor): string {
    return this.hasImageVideo(e) ? `${this.imageStorage}ivideos/${this.encodeName(e.data3)}` : '';
  }

  presentationVideoUrl(e: Exhibitor): string {
    return this.hasPresentationVideo(e) ? `${this.imageStorage}evideos/${this.encodeName(e.evening_video)}` : '';
  }

  trackByExhibitor(_index: number, e: Exhibitor): number {
    return e.t_exhibitorid;
  }

  /** Best-guess MIME type from a video filename extension. */
  getMimeType(filename: string): string {
    const ext = (filename || '').split('.').pop()?.toLowerCase() || '';
    switch (ext) {
      case 'webm':
        return 'video/webm';
      case 'ogg':
      case 'ogv':
        return 'video/ogg';
      case 'mov':
        return 'video/quicktime';
      case 'm4v':
        return 'video/x-m4v';
      case 'mp4':
      default:
        return 'video/mp4';
    }
  }

  /** Open the preview dialog for one exhibitor's image or presentation video. */
  openPreview(e: Exhibitor, kind: 'image' | 'presentation'): void {
    if (kind === 'image') {
      if (!this.hasImageVideo(e)) return;
      this.previewUrl = this.imageVideoUrl(e);
      this.previewMime = this.getMimeType(e.data3);
      this.previewLabel = 'Image video';
    } else {
      if (!this.hasPresentationVideo(e)) return;
      this.previewUrl = this.presentationVideoUrl(e);
      this.previewMime = this.getMimeType(e.evening_video);
      this.previewLabel = 'Presentation video';
    }
    this.previewTitle = this.displayName(e);
    this.previewOpen = true;
  }

  closePreview(): void {
    this.previewOpen = false;
    // drop the source so the <video> stops buffering/playing in the background
    this.previewUrl = '';
    this.previewMime = '';
    this.previewTitle = '';
    this.previewLabel = '';
  }
}
